import { updateProfileSchema } from '@nexo/types';
import { Avatar, Button, Field, Input, cn, useToast } from '@nexo/ui';
import { useMutation } from '@tanstack/react-query';
import { ArrowLeft, Download, Ghost, LogOut, Monitor, Moon, Sun } from 'lucide-react';
import type { SubmitEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { AppVersion } from '../../components/AppVersion';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';
import { api } from '../../lib/api';
import { isDesktop } from '../../lib/platform';
import { errorMessage, errorsFromApi, useForm, validate } from '../../lib/forms';
import { AlertSettings } from '../notifications/AlertSettings';
import { MyFeedback } from '../feedback/MyFeedback';
import { ActiveSessions } from './ActiveSessions';
import { PrivacySection } from './PrivacySection';
import { useTheme, type ThemePreference } from '../../lib/theme';
import { useAuth, useMe } from '../auth/AuthProvider';

const themes: { value: ThemePreference; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Claro', icon: Sun },
  { value: 'dark', label: 'Oscuro', icon: Moon },
  { value: 'system', label: 'Sistema', icon: Monitor },
  { value: 'halloween', label: 'Halloween', icon: Ghost },
];

export function ProfilePage() {
  const me = useMe();
  const { setUser, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [theme, setTheme] = useTheme();
  const { t } = useTranslation();
  const form = useForm({
    displayName: me.displayName,
    title: me.title ?? '',
    avatarUrl: me.avatarUrl ?? '',
  });

  const save = useMutation({
    mutationFn: api.users.updateMe,
    onSuccess: (updated) => {
      setUser(updated);
      toast({ title: t('Perfil actualizado'), tone: 'success' });
    },
    onError: (err) => {
      const fieldErrors = errorsFromApi(err);
      if (fieldErrors) form.setErrors(fieldErrors);
      else toast({ title: t('No se pudo guardar'), description: errorMessage(err), tone: 'error' });
    },
  });

  function onSubmit(event: SubmitEvent) {
    event.preventDefault();
    const { data, errors } = validate(updateProfileSchema, {
      displayName: form.values.displayName,
      title: form.values.title.trim() || null,
      avatarUrl: form.values.avatarUrl.trim() || null,
    });
    if (errors) return form.setErrors(errors);
    save.mutate(data);
  }

  return (
    <div className="min-h-full overflow-y-auto bg-canvas">
      <div className="mx-auto max-w-xl px-4 py-8">
        <button
          type="button"
          onClick={() => void navigate(-1)}
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted hover:text-text"
        >
          <ArrowLeft className="size-4" /> {t('Volver')}
        </button>

        <div className="mb-8 flex items-center gap-4">
          <Avatar
            id={me.id}
            name={form.values.displayName || me.displayName}
            src={form.values.avatarUrl || null}
            size="lg"
          />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('Tu perfil')}</h1>
            <p className="text-sm text-muted">{me.email}</p>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          noValidate
          className="flex flex-col gap-4 rounded-2xl border border-border bg-surface p-6 shadow-panel"
        >
          <Field label={t('Nombre')} error={form.errors.displayName}>
            {(props) => (
              <Input
                {...props}
                value={form.values.displayName}
                onChange={(e) => form.set('displayName', e.target.value)}
              />
            )}
          </Field>
          <Field
            label={t('Cargo o rol')}
            error={form.errors.title}
            hint={t('p. ej. Diseño, Ventas, CTO')}
          >
            {(props) => (
              <Input
                {...props}
                value={form.values.title}
                onChange={(e) => form.set('title', e.target.value)}
              />
            )}
          </Field>
          <Field label={t('URL de la foto (opcional)')} error={form.errors.avatarUrl}>
            {(props) => (
              <Input
                {...props}
                type="url"
                placeholder="https://…"
                value={form.values.avatarUrl}
                onChange={(e) => form.set('avatarUrl', e.target.value)}
              />
            )}
          </Field>
          <Button type="submit" loading={save.isPending} className="self-end">
            {t('Guardar cambios')}
          </Button>
        </form>

        <section className="mt-4 rounded-2xl border border-border bg-surface p-6 shadow-panel">
          <h2 className="mb-3 text-sm font-semibold">{t('Apariencia')}</h2>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {themes.map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                type="button"
                aria-pressed={theme === value}
                onClick={() => setTheme(value)}
                className={cn(
                  'flex flex-col items-center gap-1.5 rounded-xl border p-3 text-sm',
                  theme === value
                    ? 'border-accent bg-accent-soft text-accent'
                    : 'border-border hover:bg-surface-2',
                )}
              >
                <Icon className="size-5" />
                {t(label)}
              </button>
            ))}
          </div>
          <div className="mt-4 flex items-center justify-between gap-3 border-t border-border pt-4">
            <span className="text-sm">{t('Idioma')}</span>
            <LanguageSwitcher />
          </div>
        </section>

        <AlertSettings />

        <ActiveSessions />

        <MyFeedback />

        <PrivacySection />

        <Button
          variant="secondary"
          icon={<LogOut className="size-4" />}
          className="mt-6 w-full"
          onClick={() => void logout()}
        >
          {t('Cerrar sesión')}
        </Button>
        {!isDesktop && (
          <Link
            to="/download"
            className="mt-4 flex items-center gap-3 rounded-2xl border border-border bg-surface p-4 shadow-panel hover:bg-surface-2"
          >
            <span className="flex size-9 items-center justify-center rounded-xl bg-accent-soft text-accent">
              <Download className="size-[18px]" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium">
                {t('Descarga la app de escritorio')}
              </span>
              <span className="block text-xs text-muted">
                {t('Windows, macOS y Linux: notificaciones nativas y siempre a mano.')}
              </span>
            </span>
          </Link>
        )}
        <p className="mt-4 text-center text-xs text-subtle">
          Nexo <AppVersion /> · {isDesktop ? t('app de escritorio') : t('web')}
        </p>
      </div>
    </div>
  );
}
